import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../../lib/api";
import { PageHeader, EmptyState } from "../../components/common";
import { MapCenterControl } from "../../components/MapCenterControl";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const PERMIAN = [31.9973, -102.0779];

const FitBounds = ({ pins }) => {
  const map = useMap();
  useEffect(() => {
    if (pins.length > 0) {
      map.fitBounds(L.latLngBounds(pins.map((p) => [p.lat, p.lng])).pad(0.3));
    }
  }, [pins, map]);
  return null;
};

export default function FracMap() {
  const [pins, setPins] = useState([]);
  const [unmapped, setUnmapped] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/fractank/dispatches"), api.get("/locations")])
      .then(([d, l]) => {
        const byName = {};
        l.data.forEach((loc) => {
          if (loc.lat != null && loc.lng != null) byName[loc.name] = loc;
        });
        const hasGps = (x) => x.lat != null && x.lng != null;
        const onRent = d.data.filter((x) => x.status === "on_rent");
        const mapped = onRent.filter((x) => hasGps(x) || byName[x.delivery_location]);
        const groups = {};
        mapped.forEach((x) => {
          groups[x.delivery_location] = groups[x.delivery_location] || [];
          groups[x.delivery_location].push(x);
        });
        const spread = [];
        Object.entries(groups).forEach(([name, group]) => {
          group.forEach((d, i) => {
            const loc = hasGps(d) ? { lat: d.lat, lng: d.lng } : byName[name];
            let lat = loc.lat, lng = loc.lng;
            if (group.length > 1) {
              const angle = (2 * Math.PI * i) / group.length;
              const r = 0.0012;
              lat += r * Math.cos(angle);
              lng += (r / Math.cos((loc.lat * Math.PI) / 180)) * Math.sin(angle);
            }
            spread.push({ ...d, lat, lng });
          });
        });
        setPins(spread);
        setUnmapped(onRent.filter((x) => !hasGps(x) && !byName[x.delivery_location]));
      })
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const tankCount = pins.reduce((sum, d) => sum + Math.max((d.asset_ids || []).length, 1), 0) + unmapped.reduce((sum, d) => sum + Math.max((d.asset_ids || []).length, 1), 0);

  return (
    <div data-testid="frac-map-page">
      <PageHeader
        title="Tanks On Rent — Map"
        subtitle={`${tankCount} tank${tankCount === 1 ? "" : "s"} on rent at ${pins.length + unmapped.length} dispatch${pins.length + unmapped.length === 1 ? "" : "es"} (${new Set([...pins, ...unmapped].map((d) => d.delivery_location)).size} locations)`}
        testId="frac-map-header"
      />

      {pins.length === 0 && unmapped.length === 0 ? (
        <EmptyState message="No tanks currently on rent" testId="frac-map-empty" />
      ) : (
        <>
          <div className="bg-card border border-slate-700/60 rounded-lg overflow-hidden" data-testid="frac-map-container">
            <MapContainer center={PERMIAN} zoom={8} style={{ height: "520px", width: "100%" }}>
              <FitBounds pins={pins} />
              <MapCenterControl pins={pins} testId="frac-map" />
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {pins.map((d) => (
                <Marker key={d.id} position={[d.lat, d.lng]} data-testid={`frac-map-marker-${d.id}`}>
                  <Popup>
                    <div className="text-xs space-y-1">
                      <p className="font-semibold">{d.dispatch_number} — {d.customer_name}</p>
                      <p>{d.delivery_location || d.geo_address}</p>
                      {(d.asset_tags || []).length > 0 && <p>Tanks: {d.asset_tags.join(", ")}</p>}
                      {d.driver_name && <p>Driver: {d.driver_name}</p>}
                      {d.company_man && <p data-testid={`frac-map-requester-${d.id}`}>Requester: {d.company_man}</p>}
                      {d.billing_start_date && <p>Billing since {fmtDate(d.billing_start_date)}</p>}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
          {unmapped.length > 0 && (
            <p className="mt-3 text-xs font-mono text-slate-500" data-testid="frac-map-unmapped">
              Not mapped (no GPS on location): {unmapped.map((d) => `${d.dispatch_number} (${d.delivery_location})`).join(", ")}
            </p>
          )}
        </>
      )}
    </div>
  );
}
